import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LibraryCard } from '@/features/library/components/LibraryCard';
import { bookStatusLabels } from '@/features/library/utils/status';
import { continueBook, getBook, type BookDetail } from '@/features/library/services/booksApi';
import { chapterLabel, untitledChapterName } from '@/shared/utils/chapters';
import { useProcessing } from '@/app/providers/processing';
import { ApiError } from '@/shared/api/apiClient';
import { safeErrorMessage } from '@/shared/utils/errors';
import { logger } from '@/shared/utils/logger';
import { ArrowLeftIcon, Button, ButtonLink, ProgressBar, Spinner } from '@/shared/ui';
import { ErrorPage } from '@/shared/ui/ErrorPage';

/**
 * One book: its chapter videos in reading order. Each is an ordinary video card,
 * so retrying, cancelling, deleting and opening a chapter work exactly as they do
 * in the library grid.
 */
export default function BookPage() {
  const { bookId = '' } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<BookDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const { jobs, deleteJob, trackJobs } = useProcessing();

  const load = useCallback(
    (signal: { cancelled: boolean } = { cancelled: false }) => {
      setLoading(true);
      getBook(bookId)
        .then((result) => {
          if (signal.cancelled) return;
          setBook(result);
          setError(null);
          // A chapter the server started on its own (when the one before it was
          // approved) is unknown to this session until now; track it like any other.
          trackJobs(result.chapters);
        })
        .catch((err: unknown) => {
          if (signal.cancelled) return;
          logger.error('Could not load book', err);
          setError(
            err instanceof ApiError && err.status === 404
              ? 'This book doesn’t exist, or its videos were all deleted.'
              : safeErrorMessage(err, 'Couldn’t load this book. Please try again.'),
          );
        })
        .finally(() => {
          if (!signal.cancelled) setLoading(false);
        });
    },
    [bookId, trackJobs],
  );

  // Reload when one of the user's jobs changes state, as the library does, so a
  // chapter finishing while the page is open shows up as ready.
  const jobSignature = useMemo(() => jobs.map((job) => `${job.id}:${job.status}`).join('|'), [jobs]);

  useEffect(() => {
    const signal = { cancelled: false };
    load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, jobSignature]);

  // A chapter is labelled with its part only when it was split into several.
  const splitChapters = useMemo(() => {
    const split = new Set<number>();
    const refs = [...(book?.chapters ?? []).map((item) => item.chapter), ...(book?.upcoming ?? [])];
    for (const ref of refs) {
      if (ref && ref.partIndex > 0) split.add(ref.chapterIndex);
    }
    return split;
  }, [book]);

  const startNext = async () => {
    setStarting(true);
    setStartError(null);
    try {
      await continueBook(bookId);
      load();
    } catch (err: unknown) {
      setStartError(safeErrorMessage(err, 'Couldn’t start the next chapter. Please try again.'));
    } finally {
      setStarting(false);
    }
  };

  if (error && !book) {
    return (
      <ErrorPage
        title="Book unavailable"
        message={error}
        backLabel="Back to Library"
        backTo="/library"
        onRetry={() => load()}
      />
    );
  }

  if (!book) {
    return (
      <div className="library__loading">
        <Spinner size={26} label="Loading book" />
      </div>
    );
  }

  return (
    <section className="book page--wide">
      <ButtonLink to="/library" variant="ghost" size="sm" iconLeft={<ArrowLeftIcon size={16} />}>
        Library
      </ButtonLink>

      <header className="book__header">
        <h1 className="book__title">{book.title}</h1>
        <p className="book__summary">
          {book.completedCount} of {book.videoCount} {book.videoCount === 1 ? 'video' : 'videos'} ready
          {book.status !== 'completed' ? ` · ${bookStatusLabels[book.status]}` : ''}
        </p>
        <ProgressBar
          value={book.videoCount ? book.completedCount / book.videoCount : 0}
          label={`${book.completedCount} of ${book.videoCount} videos ready`}
        />
      </header>

      {book.canContinue && book.upcoming.length > 0 ? (
        <div className="book__next">
          <Button variant="primary" onClick={() => void startNext()} disabled={starting}>
            {starting ? 'Starting…' : `Start ${chapterLabel(book.upcoming[0], splitChapters.has(book.upcoming[0].chapterIndex))}`}
          </Button>
          <span className="book__next-hint">
            Each chapter is priced and approved on its own before it renders.
          </span>
          {startError ? (
            <span className="book__next-error" role="alert">
              {startError}
            </span>
          ) : null}
        </div>
      ) : null}

      {loading ? <span className="sr-only" role="status">Refreshing</span> : null}

      <ol className="library__grid library__grid--list book__chapters">
        {book.chapters.map((item) => (
          <li key={item.id} className="book__chapter">
            {item.chapter ? (
              <p className="book__chapter-label">
                {chapterLabel(item.chapter, splitChapters.has(item.chapter.chapterIndex))}
              </p>
            ) : null}
            <LibraryCard
              item={item}
              // Until it has a title of its own it is called by the chapter, named just above.
              hideTitle={item.chapter ? item.title === untitledChapterName(item.chapter) : false}
              onDelete={async () => {
                await deleteJob(item.id);
                load();
              }}
            />
            {/* Approved, but a book renders one chapter at a time. */}
            {item.status === 'queued' && item.waitingFor ? (
              <p className="book__waiting-note">
                Starts once {chapterLabel(item.waitingFor, splitChapters.has(item.waitingFor.chapterIndex))} has
                finished
              </p>
            ) : null}
          </li>
        ))}
        {book.upcoming.map((chapter) => (
          <li key={`${chapter.chapterIndex}.${chapter.partIndex}`} className="book__chapter book__chapter--upcoming">
            <p className="book__chapter-label">
              {chapterLabel(chapter, splitChapters.has(chapter.chapterIndex))}
            </p>
            <p className="book__upcoming-note">
              Not started · {chapter.wordCount.toLocaleString()} words
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}
